import React from 'react';
import ReportingClassesReportPage from './ReportingClassesReportPage';
import {
  SORT_TYPES, CLASSES_SALES_COLUMNS, CLASSES_SALES_MONEY_COLUMN_IDS, getDefaultVisibleColumns, getDefaultHiddenColumns,
} from '../constants/classesSalesColumnsConfig';
import { buildClassesSalesFilterGroups, CLASSES_SALES_FILTER_IDS, pickFilterParams } from '../constants/classesFiltersConfig';
import { compactCurrency, formatVenueDateTime } from '../utils/reportingUtils';

const countCard = (label, key, current, prior) => ({
  label,
  value: (current[key] || 0).toLocaleString(),
  current: current[key] || 0,
  prior: (prior && prior[key]) || 0,
});

const moneyCard = (label, key, current, prior) => ({
  label,
  value: compactCurrency(current[key] || 0),
  current: current[key] || 0,
  prior: (prior && prior[key]) || 0,
});

const CLASSES_SALES_CONFIG = {
  pageTitle: 'Class Sales',
  pageSubtitle: "One row per class. Net is your share after fees, refunds and chargebacks, worked out like the class page's Net revenue; with a range that covers all of a class's sales, the two match. Attendance counts active registrations only.",
  sidebarKey: 'classes_sales',
  defaultPreset: 'year_to_date',
  dateModeOptions: [
    { key: 'order_date', label: 'Purchase Date' },
    { key: 'event_date', label: 'Class Date' },
  ],
  urlFilterIds: CLASSES_SALES_FILTER_IDS,

  sortTypes: SORT_TYPES,
  localStorageKey: (teamId) => `reportingClassesSalesColumns_${teamId}`,
  firstColumnId: 'class_name',
  defaultSortRules: [{ field: 'first_session_at', direction: 'desc' }],

  kpiFromDetailResponse: true,
  salesDataKey: null,
  venueSeriesKey: null,
  exportBasePath: (teamId) => `/teams/${teamId}/reporting/classes/sales/export`,
  detailEndpoint: (teamId) => `/teams/${teamId}/reporting/classes/sales/data.json`,
  detailResponseKey: 'classes',
  detailErrorMessage: 'Failed to load class sales.',
  detailTableTitle: 'Classes',
  emptyMessage: 'No classes in this range.',
  searchPlaceholder: 'Search classes, templates, venues...',
  paginationLabel: 'classes',
  itemLabel: (count) => `class${count !== 1 ? 'es' : ''}`,

  buildKpiCards: (current, prior) => [
    countCard('Classes', 'classes', current, prior),
    countCard('Registrations', 'registrations', current, prior),
    countCard('Comps', 'comps', current, prior),
    moneyCard('Gross', 'gross', current, prior),
    moneyCard('Refunds & Disputes', 'refunds', current, prior),
    moneyCard('Net', 'net', current, prior),
    countCard('Attended', 'attended', current, prior),
    {
      label: 'Attendance Rate',
      value: `${current.attendance_rate || 0}%`,
      current: current.attendance_rate || 0,
      prior: (prior && prior.attendance_rate) || 0,
    },
  ],
  kpiGridColumns: 4,

  overTimeKey: null,
  topCharts: [],
  groupByOptions: [],
  groupByDefault: null,

  renderCellValue: (col, row, { currencyFormatter: cf, team }) => {
    if (CLASSES_SALES_MONEY_COLUMN_IDS.includes(col.id)) return cf.format(row[col.id] || 0);
    switch (col.id) {
      case 'class_name': return team?.id ? <a href={`/teams/${team.id}/classes/${row.id}`}>{row.class_name}</a> : row.class_name;
      case 'template_name': return row.template_name || '—';
      case 'venue_name': return row.venue_name || '—';
      case 'first_session_at': return <span className="text-nowrap">{formatVenueDateTime(row.first_session_at, row.time_zone)}</span>;
      case 'status': return row.status_label || '—';
      case 'capacity': return row.capacity ?? 0;
      case 'spots_taken': return row.spots_taken ?? 0;
      case 'registrations': return (row.registrations || 0).toLocaleString();
      case 'comps': return (row.comps || 0).toLocaleString();
      case 'attended': return (row.attended || 0).toLocaleString();
      case 'attendance_rate': return `${row.attendance_rate || 0}%`;
      default: return '—';
    }
  },

  filterTitle: 'Filter Classes',
  filterSubtitle: 'Apply filters to narrow class sales.',
};

const ReportingClassesSalesPage = (props) => (
  <ReportingClassesReportPage
    {...props}
    config={{
      ...CLASSES_SALES_CONFIG,
      columns: CLASSES_SALES_COLUMNS,
      getDefaultVisibleColumns,
      getDefaultHiddenColumns,
      buildDetailApiFilters: pickFilterParams(CLASSES_SALES_FILTER_IDS),
    }}
    reportPath="sales"
    filterErrorMessage="Could not load the class filters. Reload the page to try again."
    buildFilterGroups={buildClassesSalesFilterGroups}
  />
);

export default ReportingClassesSalesPage;
